// Карта мира v8 «Рельефный архипелаг» в клетках (общая для сервера и клиента): девять островов
// (один — только по морю), короткие мосты, плато с обрывами и подъёмами, горные хребты, реки, леса, дороги.
// Источник — scripts/mapgen-v8/gen.py → mapV8Data.ts (сетка 645×404 RLE). Мир другого размера — та же
// сетка в масштабе size / 645 (ближайшая клетка).
import { MAP_V8 as V } from './mapV8Data.js';
import type { Point } from './types.js';

/** Ширина мира в клетках. */
export const ARCHIPELAGO_SIZE = 645;
/** Ширина и высота мира в «нативных» единицах — для v8 это клетки. */
export const NATIVE = { width: V.size, height: V.rows } as const;
/** Мир в экранных px при обычном масштабе карты (≈40 px на клетку, как в archipelago-v7). */
export const WORLD_PX = { width: 25984, height: (25984 * V.rows) / V.size } as const;

/** Коды клеток сетки. Список только дополняется: сетки и маршруты хранят индексы. */
export const CELL = {
  SEA: 0,
  LAND: 1,
  COAST: 2,
  RIVER: 3,
  MOUNTAIN: 4,
  /** Мост над морем: войска идут по нему, корабли проходят под ним. */
  BRIDGE: 5,
  /** Малый мост дороги через реку. */
  CROSSING: 6,
  FOREST: 7,
  ROAD: 8,
  /** Площадь места силы в центре мира (проходима, строить нельзя). */
  PLAZA: 9,
  /** За краем мира. */
  OUTSIDE: 10,
  /** Обрыв плато или скалистый берег: не пройти, войска обходят. */
  CLIFF: 11,
  /** Подъём на плато / крутой склон: пройти можно, но медленнее. */
  HILL: 12,
} as const;

/** Клеток по вертикали в мире шириной size. */
export function worldRows(size: number): number {
  return Math.min(size, Math.ceil((size * NATIVE.height) / NATIVE.width));
}

/** Перевод нативной точки (клетки карты v8) в клетки мира шириной size. */
export function nativeToTile(x: number, y: number, size: number): Point {
  const k = size / NATIVE.width;
  return { x: x * k, y: y * k };
}

export interface ArchipelagoGrid {
  size: number;
  rows: number;
  /** Код клетки (CELL), индекс y * size + x, только y < rows. */
  cells: Uint8Array;
  /** Сколько из 3×3 точек клетки — суша острова (0…9). */
  land: Uint8Array;
}

const B64 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/';

/** base64 → байты (без atob/Buffer: одинаково в Node и браузере). */
function fromBase64(b64: string): Uint8Array {
  const clean = b64.replace(/=+$/, '');
  const out = new Uint8Array(Math.floor((clean.length * 3) / 4));
  let o = 0;
  for (let i = 0; i < clean.length; i += 4) {
    const a = B64.indexOf(clean[i]!);
    const b = B64.indexOf(clean[i + 1] ?? 'A');
    const c = i + 2 < clean.length ? B64.indexOf(clean[i + 2]!) : 0;
    const d = i + 3 < clean.length ? B64.indexOf(clean[i + 3]!) : 0;
    const n = (a << 18) | (b << 12) | (c << 6) | d;
    out[o++] = (n >> 16) & 255;
    if (i + 2 < clean.length) out[o++] = (n >> 8) & 255;
    if (i + 3 < clean.length) out[o++] = n & 255;
  }
  return out.subarray(0, o);
}

/** Пары (значение, длина) → массив клеток. */
function decodeRle(b64: string, length: number): Uint8Array {
  const out = new Uint8Array(length);
  const bytes = fromBase64(b64);
  let o = 0;
  for (let i = 0; i + 1 < bytes.length; i += 2) {
    out.fill(bytes[i]!, o, o + bytes[i + 1]!);
    o += bytes[i + 1]!;
  }
  return out;
}

let raw: { cells: Uint8Array; land: Uint8Array; islands: Uint8Array } | null = null;
function base() {
  if (!raw) {
    const n = V.size * V.rows;
    raw = { cells: decodeRle(V.cells, n), land: decodeRle(V.land, n), islands: decodeRle(V.islands, n) };
    // Мосты в сетке — полоса ≈4 клетки вдоль оси: путь по диагональному мосту не «срезает угол» через воду
    const cells = raw.cells;
    for (const b of V.bridges) {
      const [x0, y0, x1, y1] = b as [number, number, number, number];
      const len = Math.hypot(x1 - x0, y1 - y0) || 1;
      const nx = (-(y1 - y0) / len) * 0.5;
      const ny = ((x1 - x0) / len) * 0.5;
      for (const o of [-3, -2, -1, 1, 2, 3])
        traceCells([x0 + nx * o, y0 + ny * o, x1 + nx * o, y1 + ny * o], (x, y) => {
          const i = y * V.size + x;
          if (x >= 0 && y >= 0 && x < V.size && y < V.rows && cells[i] === CELL.SEA) cells[i] = CELL.BRIDGE;
        });
    }
    // Одиночные клетки воды внутри суши (корабль туда не доплывёт) — суша
    for (let y = 1; y < V.rows - 1; y++)
      for (let x = 1; x < V.size - 1; x++) {
        const i = y * V.size + x;
        if (cells[i] !== CELL.SEA) continue;
        const nav = (j: number) => cells[j] === CELL.SEA || cells[j] === CELL.BRIDGE;
        if (!nav(i - 1) && !nav(i + 1) && !nav(i - V.size) && !nav(i + V.size)) cells[i] = CELL.LAND;
      }
  }
  return raw;
}

/** Нативная клетка под клеткой (x, y) мира шириной size. */
function nativeIndex(x: number, y: number, size: number): number {
  const k = V.size / size;
  const nx = Math.min(V.size - 1, Math.floor((x + 0.5) * k));
  const ny = Math.min(V.rows - 1, Math.floor((y + 0.5) * k));
  return ny * V.size + nx;
}

const gridCache = new Map<number, ArchipelagoGrid>();

export function archipelagoGrid(size: number): ArchipelagoGrid {
  const hit = gridCache.get(size);
  if (hit) return hit;
  const b = base();
  const rows = worldRows(size);
  let cells: Uint8Array;
  let land: Uint8Array;
  if (size === V.size) {
    cells = b.cells;
    land = b.land;
  } else {
    cells = new Uint8Array(size * rows);
    land = new Uint8Array(size * rows);
    for (let y = 0; y < rows; y++)
      for (let x = 0; x < size; x++) {
        const j = nativeIndex(x, y, size);
        cells[y * size + x] = b.cells[j]!;
        land[y * size + x] = b.land[j]!;
      }
  }
  const grid = { size, rows, cells, land };
  gridCache.set(size, grid);
  return grid;
}

/**
 * Обход ломаной по клеткам без «диагональных дыр»: соседние клетки следа всегда имеют общую
 * сторону, поэтому река или мост в одну клетку шириной не пропускает шаг по диагонали.
 */
export function traceCells(points: readonly number[], cb: (x: number, y: number) => void): void {
  let lx = Number.NaN;
  let ly = Number.NaN;
  for (let i = 0; i + 3 < points.length; i += 2) {
    const ax = points[i]!;
    const ay = points[i + 1]!;
    const bx = points[i + 2]!;
    const by = points[i + 3]!;
    const steps = Math.max(1, Math.ceil(Math.hypot(bx - ax, by - ay) / 0.1));
    for (let s = i === 0 ? 0 : 1; s <= steps; s++) {
      const x = ax + ((bx - ax) * s) / steps;
      const y = ay + ((by - ay) * s) / steps;
      const cx = Math.floor(x);
      const cy = Math.floor(y);
      if (cx === lx && cy === ly) continue;
      if (!Number.isNaN(lx) && cx !== lx && cy !== ly) {
        const d1 = (cx + 0.5 - x) ** 2 + (ly + 0.5 - y) ** 2;
        const d2 = (lx + 0.5 - x) ** 2 + (cy + 0.5 - y) ** 2;
        if (d1 < d2) cb(cx, ly);
        else cb(lx, cy);
      }
      cb(cx, cy);
      lx = cx;
      ly = cy;
    }
  }
}

/** Код клетки; за пределами мира — OUTSIDE. */
export function cellAt(x: number, y: number, size: number): number {
  const g = archipelagoGrid(size);
  if (x < 0 || y < 0 || x >= size || y >= g.rows) return CELL.OUTSIDE;
  const ix = Math.floor(x);
  const iy = Math.floor(y);
  if (ix >= size || iy >= g.rows) return CELL.OUTSIDE;
  return g.cells[iy * size + ix]!;
}

/** Можно ли пройти войскам. */
export function isWalkableCell(c: number): boolean {
  return (
    c === CELL.LAND ||
    c === CELL.COAST ||
    c === CELL.FOREST ||
    c === CELL.ROAD ||
    c === CELL.BRIDGE ||
    c === CELL.CROSSING ||
    c === CELL.PLAZA ||
    c === CELL.HILL
  );
}

/** Можно ли пройти кораблям (под мостами тоже). */
export function isNavigableCell(c: number): boolean {
  return c === CELL.SEA || c === CELL.BRIDGE;
}

export type SlotKind = 'player' | 'resource' | 'bandit' | 'abandoned';

const slotCache = new Map<string, Point[]>();

/**
 * Места локаций из раскладки карты (клетки), без повторов. Для 'player' — по порядку от начала
 * координат: первая суша у (0, 0), затем всё дальше (новые игроки селятся рядом друг с другом).
 */
export function archipelagoSlots(size: number, kind: SlotKind): Point[] {
  const key = `${size}:${kind}`;
  const hit = slotCache.get(key);
  if (hit) return hit;
  const want = (V.slotKinds as readonly string[]).indexOf(kind);
  const k = size / NATIVE.width;
  const seen = new Set<string>();
  const res: Point[] = [];
  for (let i = 0; i + 2 < V.slots.length; i += 3) {
    if (V.slots[i] !== want) continue;
    const p = { x: Math.floor((V.slots[i + 1]! + 0.5) * k), y: Math.floor((V.slots[i + 2]! + 0.5) * k) };
    const id = `${p.x}:${p.y}`;
    if (seen.has(id)) continue;
    seen.add(id);
    res.push(p);
  }
  if (kind === 'player') res.sort((a, b) => a.x * a.x + a.y * a.y - (b.x * b.x + b.y * b.y) || a.y - b.y);
  slotCache.set(key, res);
  return res;
}

/** Клетка места силы (центр мира для политики: столица). */
export function objectiveTile(size: number): Point {
  const p = nativeToTile(V.objective.x, V.objective.y, size);
  return { x: Math.floor(p.x), y: Math.floor(p.y) };
}

/** Острова мира; названия — в переводах game:islands.<id>. Список только дополняется. */
export const ISLAND_IDS = [
  'central',
  'north',
  'northwest',
  'northeast',
  'east',
  'southeast',
  'southwest',
  'west',
  'remote',
] as const;
export type IslandId = (typeof ISLAND_IDS)[number];

/**
 * Остров под клеткой (x, y) мира шириной size; в море — ближайший остров (в пределах maxSeaTiles,
 * не дальше 40 клеток), иначе null. sea = true, если клетка в воде («у берегов …»).
 */
export function islandAt(
  x: number,
  y: number,
  size: number,
  maxSeaTiles = 40,
): { id: IslandId; sea: boolean } | null {
  if (x < 0 || y < 0 || x >= size || y >= worldRows(size)) return null;
  const v = base().islands[nativeIndex(x, y, size)]!;
  if (!v) return null;
  const sea = v > 16;
  const id = V.islandIds[(sea ? v - 16 : v) - 1] as IslandId | undefined;
  if (!id) return null;
  if (sea && maxSeaTiles < 40) {
    // Ближе ли остров, чем maxSeaTiles: ищем сушу этого острова в квадрате
    const r = Math.ceil(maxSeaTiles);
    const g = archipelagoGrid(size);
    let near = false;
    for (let dy = -r; dy <= r && !near; dy++)
      for (let dx = -r; dx <= r; dx++) {
        const cx = x + dx;
        const cy = y + dy;
        if (cx < 0 || cy < 0 || cx >= size || cy >= g.rows || dx * dx + dy * dy > r * r) continue;
        if (base().islands[nativeIndex(cx, cy, size)] === v - 16) {
          near = true;
          break;
        }
      }
    if (!near) return null;
  }
  return { id, sea };
}

/** Мосты карты в нативных клетках: x0, y0, x1, y1 (концы на суше, с учётом половины клетки). */
export const MAP_BRIDGES: readonly (readonly number[])[] = V.bridges;
